import React, { useState } from 'react';
import styled from 'styled-components';
import { authService } from '../../services/api.service';
import { API_URL } from '../../config';

const FormContainer = styled.div`
  max-width: 400px;
  margin: 0 auto;
  padding: 2rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 1.5rem;
  color: #333;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #0084ff;
    box-shadow: 0 0 0 2px rgba(0, 132, 255, 0.2);
  }
`;

const Button = styled.button`
  padding: 0.75rem;
  background-color: #0084ff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #0073e6;
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #e53935;
  margin-top: 1rem;
  padding: 0.5rem;
  background-color: #ffebee;
  border-radius: 4px;
  text-align: center;
`;

const WarningMessage = styled.div`
  color: #f57c00;
  margin-top: 1rem;
  padding: 0.5rem;
  background-color: #fff3e0;
  border-radius: 4px;
  text-align: center;
`;

const SwitchText = styled.p`
  text-align: center;
  margin-top: 1rem;
`;  

const ForgotPasswordLink = styled.p`
  text-align: center;
  margin-top: 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const SwitchLink = styled.span`
  color: #0084ff;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const LoginForm = ({ onLogin, switchToRegister, switchToForgotPassword }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [inactiveAccount, setInactiveAccount] = useState(false);
  const [inactiveEmail, setInactiveEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInactiveAccount(false);
    setResendSuccess(false);
    
    // Validate form
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    try {
      setIsLoading(true);
      const response = await authService.login(formData.email, formData.password);
      
      if (response.success) {
        onLogin(response.user);
      } else if (response.inactive) {
        // Handle inactive account
        setInactiveAccount(true);
        setInactiveEmail(response.email || formData.email);
      } else {
        setError(response.error || response.message || 'Login failed');
      }
    } catch (error) {
      if (error.response?.data?.inactive) {
        // Handle inactive account from error response
        setInactiveAccount(true);
        setInactiveEmail(error.response.data.email || formData.email);
      } else {
        setError(error.response?.data?.message || error.message || 'An error occurred during login');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleResendActivation = async () => {
    try {
      setResendLoading(true);
      setError('');
      setResendSuccess(false);
      
      const response = await fetch(`${API_URL}/activation/resend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: inactiveEmail }),
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setResendSuccess(true);
      } else {
        setError(data.message || 'Failed to resend activation email');
      }
    } catch (error) {
      setError('An error occurred while requesting a new activation link');
    } finally {
      setResendLoading(false);
    }
  };
  
  return (
    <FormContainer>
      <Title>Log In</Title>
      
      {inactiveAccount ? (
        <div>
          <WarningMessage>
            Your account has not been activated yet. Please check your email for the activation link or request a new one.
          </WarningMessage>
          
          {resendSuccess ? (
            <ErrorMessage style={{ color: '#4caf50', backgroundColor: '#e8f5e9' }}>
              Activation email sent successfully! Please check your inbox.
            </ErrorMessage>
          ) : error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : null}
          
          <Button 
            onClick={handleResendActivation} 
            disabled={resendLoading}
            style={{ marginTop: '1rem' }}
          >
            {resendLoading ? 'Sending...' : 'Resend Activation Email'}
          </Button>
          
          <SwitchText>
            <SwitchLink onClick={() => {
              setInactiveAccount(false);
              setError('');
              setResendSuccess(false);
            }}>
              Back to Login
            </SwitchLink>
          </SwitchText>
        </div>
      ) : (
        <>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="email">Email</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email address"
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="password">Password</Label>
              <Input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                required
              />
            </FormGroup>
            
            {error && <ErrorMessage>{error}</ErrorMessage>}
            
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Logging in...' : 'Log In'}
            </Button>
          </Form>
          
          <ForgotPasswordLink>
            <SwitchLink onClick={switchToForgotPassword}>Forgot your password?</SwitchLink>
          </ForgotPasswordLink>
          
          <SwitchText>
            Don't have an account?{' '}
            <SwitchLink onClick={switchToRegister}>Register</SwitchLink>
          </SwitchText>
        </>
      )}
    </FormContainer>
  );
};

export default LoginForm;
